import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GeocoderType, LatLngType } from "@repo/ping-react-js";
import { api } from "services/appSlice";

type GeocodingInitialState = {
  /**
   * Currently hovered marker in geocoding mode
   */
  hoveredMarker: string | null;

  /**
   * Currently selected geocoder in geocoding mode (for tooltip display)
   */
  selectedGeocoder: GeocoderType | null;

  /**
   * Pre-merge geocoding sources for geocoding mode
   * Contains all geocoding results from different providers
   */
  geocoderData: GeocoderType[];

  /**
   * Annotations for the map
   */
  annotations: Record<string, any>;

  /**
   * User location for geocoding mode
   */
  userLocation: LatLngType | null;

  /**
   * Original SOV for geocoding mode
   */
  originalSOV: GeocoderType | null;

  /**
   * Has user location for geocoding mode
   */
  hasUserLocation: boolean;

  /**
   * User location mode for geocoding mode
   */
  userLocationMode: boolean;

  /**
   * Last geocoder search result from the search bar
   */
  lastGeocoderSearch: { address: string; latLng: LatLngType } | null;
};

const initialState: GeocodingInitialState = {
  hoveredMarker: null,
  selectedGeocoder: null,
  geocoderData: [],
  annotations: {},
  userLocation: null,
  originalSOV: null,
  hasUserLocation: false,
  userLocationMode: false,
  lastGeocoderSearch: null,
};

const geocodingSlice = createSlice({
  name: "geo",
  initialState,
  reducers: {
    setHoveredMarker(state, action: PayloadAction<string | null>) {
      state.hoveredMarker = action.payload;
    },
    setSelectedGeocoder(state, action: PayloadAction<GeocoderType | null>) {
      state.selectedGeocoder = action.payload;
    },
    setAnnotations(state, action) {
      state.annotations = {
        by_user: {
          lat: action.payload?.latitude,
          lng: action.payload?.longitude,
        },
        by_aoa: {},
      };
    },
    setUserLocation(state, action: PayloadAction<LatLngType | null>) {
      state.userLocation = action.payload;
    },
    setHasUserLocation(state, action: PayloadAction<boolean>) {
      state.hasUserLocation = action.payload;
    },
    setUserLocationMode(state, action: PayloadAction<boolean>) {
      state.userLocationMode = action.payload;
    },
    setLastGeocoderSearch(
      state,
      action: PayloadAction<{ address: string; latLng: LatLngType } | null>,
    ) {
      state.lastGeocoderSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.getBuildingGeocodingSources.matchFulfilled,
      (state, action: PayloadAction<GeocoderType[]>) => {
        state.geocoderData = action.payload;

        state.originalSOV =
          action.payload.find((blg) => blg.source === "PG") || null;
      },
    );
  },
});

const { actions, reducer } = geocodingSlice;

export const {
  setHoveredMarker,
  setSelectedGeocoder,
  setAnnotations,
  setUserLocation,
  setHasUserLocation,
  setUserLocationMode,
  setLastGeocoderSearch,
} = actions;

export default reducer;
